import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  SprengnetterValuationHubValuationReportModelResponseAvmPriceResponse as AvmPriceResponse,
  SprengnetterValuationHubValuationPackageModelResponseDemandScoreAvmResponse as DemandScoreResponse,
  GetServiceApiValuationReportRealEstateContactApiResponse,
  SprengnetterValuationHubProsaWebModelResponseProsaWebResponse as ProsaWebResponse,
  SprengnetterValuationHubValuationReportModelResponseSonePriceResponse as SonePriceResponse,
  ValuationHubCoreModelDtoValuationReportConfigurationDto,
  ValuationHubCoreModelRealEstateAddress,
  ValuationHubCoreModelRealEstateAvmEquipmentPriceRange,
} from "@/api/generatedApi";
import { PropertyInformation } from "@/types/refineRealEstate";

export interface ValuationReportState {
  address: ValuationHubCoreModelRealEstateAddress | null;
  propertyInformation: PropertyInformation | null;
  equipmentPriceRange: ValuationHubCoreModelRealEstateAvmEquipmentPriceRange[] | null;
  selectedEquipmentPriceRange: ValuationHubCoreModelRealEstateAvmEquipmentPriceRange | null;
  avm: AvmPriceResponse | null;
  sone: SonePriceResponse | null;
  prosaWeb: ProsaWebResponse | null;
  demandScore: DemandScoreResponse | null;
  contact: GetServiceApiValuationReportRealEstateContactApiResponse | null;
  report: {
    reportFileName?: string | null;
    reportTimestamp?: string | null;
    reportAskingPrice?: number | null;
  };
  reportConfigurationDraft: ValuationHubCoreModelDtoValuationReportConfigurationDto | null;
}

const initialState: ValuationReportState = {
  address: null,
  propertyInformation: null,
  equipmentPriceRange: null,
  selectedEquipmentPriceRange: null,
  avm: null,
  sone: null,
  prosaWeb: null,
  demandScore: null,
  contact: null,
  report: {
    reportFileName: null,
    reportTimestamp: null,
    reportAskingPrice: null,
  },
  reportConfigurationDraft: null,
};

export const valuationReportSlice = createSlice({
  name: "valuationReport",
  initialState,
  reducers: {
    setRealEstateData: (state, action: PayloadAction<Partial<ValuationReportState>>) => {
      return {
        ...state,
        address: "address" in action.payload ? action.payload.address ?? null : state.address,
        propertyInformation:
          "propertyInformation" in action.payload
            ? action.payload.propertyInformation ?? null
            : state.propertyInformation,
        equipmentPriceRange:
          "equipmentPriceRange" in action.payload
            ? action.payload.equipmentPriceRange ?? null
            : state.equipmentPriceRange,
        selectedEquipmentPriceRange:
          "selectedEquipmentPriceRange" in action.payload
            ? action.payload.selectedEquipmentPriceRange ?? null
            : state.selectedEquipmentPriceRange,
      };
    },
    setContact: (state, action: PayloadAction<GetServiceApiValuationReportRealEstateContactApiResponse | null>) => {
      state.contact = action.payload;
    },
    updatePropertyDetails: (state, action: PayloadAction<Partial<PropertyInformation>>) => {
      if (!state.propertyInformation) {
        state.propertyInformation = {
          generalInfo: {},
          plotData: {},
          buildingData: {},
          energyAndPhotovoltaic: {},
          buildingStandard: {},
          modernization: {},
          financialAndRentalInfo: {},
        } as PropertyInformation;
      }

      state.propertyInformation = {
        ...state.propertyInformation,
        generalInfo: action.payload.generalInfo
          ? {
              ...(state.propertyInformation.generalInfo || {}),
              ...action.payload.generalInfo,
            }
          : state.propertyInformation.generalInfo,
        plotData: action.payload.plotData
          ? {
              ...(state.propertyInformation.plotData || {}),
              ...action.payload.plotData,
            }
          : state.propertyInformation.plotData,
        buildingData: action.payload.buildingData
          ? {
              ...(state.propertyInformation.buildingData || {}),
              ...action.payload.buildingData,
            }
          : state.propertyInformation.buildingData,
        energyAndPhotovoltaic: action.payload.energyAndPhotovoltaic
          ? {
              ...(state.propertyInformation.energyAndPhotovoltaic || {}),
              ...action.payload.energyAndPhotovoltaic,
            }
          : state.propertyInformation.energyAndPhotovoltaic,
        buildingStandard: action.payload.buildingStandard
          ? {
              ...(state.propertyInformation.buildingStandard || {}),
              ...action.payload.buildingStandard,
            }
          : state.propertyInformation.buildingStandard,
        modernization: action.payload.modernization
          ? {
              ...(state.propertyInformation.modernization || {}),
              ...action.payload.modernization,
            }
          : state.propertyInformation.modernization,
        financialAndRentalInfo: action.payload.financialAndRentalInfo
          ? {
              ...(state.propertyInformation.financialAndRentalInfo || {}),
              ...action.payload.financialAndRentalInfo,
            }
          : state.propertyInformation.financialAndRentalInfo,
        propertyImages: action.payload.propertyImages
          ? {
              ...(state.propertyInformation.propertyImages || {}),
              ...action.payload.propertyImages,
            }
          : state.propertyInformation.propertyImages,
      };
    },
    updateSone: (state, action: PayloadAction<SonePriceResponse | null>) => {
      state.sone = action.payload;
    },

    updateAvm: (state, action: PayloadAction<AvmPriceResponse | null>) => {
      state.avm = action.payload;
    },

    updateProsaWeb: (state, action: PayloadAction<ProsaWebResponse | null>) => {
      state.prosaWeb = action.payload;
    },

    updateDemandScore: (state, action: PayloadAction<DemandScoreResponse | null>) => {
      state.demandScore = action.payload;
    },

    resetDerivedValuationState: (
      state,
      action: PayloadAction<{ clearSone?: boolean; clearSelectedEquipmentPriceRange?: boolean } | undefined>,
    ) => {
      state.avm = null;
      state.demandScore = null;
      state.prosaWeb = null;

      if (action.payload?.clearSone) {
        state.sone = null;
      }

      if (action.payload?.clearSelectedEquipmentPriceRange) {
        state.selectedEquipmentPriceRange = null;
      }
    },

    updateReport: (state, action: PayloadAction<Partial<ValuationReportState["report"]>>) => {
      state.report = {
        ...(state.report || {}),
        ...action.payload,
      };
    },

    updateReportConfigurationDraft: (
      state,
      action: PayloadAction<ValuationHubCoreModelDtoValuationReportConfigurationDto | null>,
    ) => {
      state.reportConfigurationDraft = action.payload;
    },
  },
});

export const {
  setRealEstateData,
  setContact,
  updatePropertyDetails,
  updateAvm,
  updateProsaWeb,
  updateDemandScore,
  resetDerivedValuationState,
  updateReport,
  updateReportConfigurationDraft,
  updateSone,
} = valuationReportSlice.actions;

export const ValuationReportReducer = valuationReportSlice.reducer;
